import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Upload, FileCode, CheckCircle2, X } from 'lucide-react';
import { toast } from 'sonner';


export const UploadDialog = ({ open, onOpenChange }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
    const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.name.endsWith('.sql')) {
        setSelectedFile(file);
        setIsComplete(false);
        toast.success('File selected successfully');
      } else {
        toast.error('Please select a valid SQL file');
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith('.sql')) {
      setSelectedFile(file);
      setIsComplete(false);
      toast.success('File selected successfully');
    } else {
      toast.error('Please drop a valid SQL file');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

const handleUpload = async () => {
  if (!selectedFile) return;

  setIsProcessing(true);
  setProgress(0);

  try {
    const formData = new FormData();
    formData.append('file', selectedFile);

    const response = await fetch('https://apifromsql.xyz/api/CustomerProject', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Upload failed: ${text}`);
    }

    const blob = await response.blob();
    if (!blob || blob.size === 0) throw new Error('Received empty file');

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = "CreatedProject.zip";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    setProgress(100);
    setIsComplete(true);
    toast.success('API generated successfully!');
  } catch (err) {
    console.error(err);
    toast.error(err.message);
  } finally {
    setIsProcessing(false);
  }
};


  const handleReset = () => {
    setSelectedFile(null);
    setIsProcessing(false);
    setProgress(0);
    setIsComplete(false);
        setIsAgreementChecked(false); // <<< EKLENDİ
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownload = () => {
    toast.success('Downloading your .NET API project...');
    // Mock download functionality
    setTimeout(() => {
      toast.success('Download complete!');
    }, 1500);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">Upload SQL Script</DialogTitle>
          <DialogDescription>
            Upload your database schema SQL file to generate a complete .NET Clean Architecture API
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {!isComplete ? (
            <>
              {/* Upload Area */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-border rounded-xl p-12 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/50 transition-all"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".sql"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-foreground font-medium mb-2">
                  Click to upload or drag and drop
                </p>
                <p className="text-sm text-muted-foreground">
                  SQL files only (.sql)
                </p>
              </div>

              {/* Selected File */}
              {selectedFile && (
                <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                  <FileCode className="w-8 h-8 text-primary" />
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReset();
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              {/* User Agreement Checkbox */} {/* <<< EKLENDİ */}
              <div className="flex items-center gap-2 mt-4">
                <input
                  type="checkbox"
                  id="userAgreement"
                  checked={isAgreementChecked}
                  onChange={(e) => setIsAgreementChecked(e.target.checked)}
                  className="w-4 h-4"
                />
                <label htmlFor="userAgreement" className="text-sm text-foreground">
                  I have read and agree to the{' '}
<a
  href="/UserAgreement"
  target="_blank"
  rel="noopener noreferrer"
  className="text-primary underline hover:text-primary-dark"
>
  Terms & Conditions
</a>
                </label>
              </div>

              {/* Processing Progress */}
              {isProcessing && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Generating API...</span>
                    <span className="text-primary font-medium">{progress}%</span>
                  </div>
                  <Progress value={progress} className="h-2" />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleUpload}
                    disabled={!(selectedFile && isAgreementChecked) || isProcessing} // <<< Düzeltilmiş
                  className="flex-1 bg-primary hover:bg-primary-dark text-primary-foreground"
                >
                  {isProcessing ? 'Processing...' : 'Generate API'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={isProcessing}
                >
                  Reset
                </Button>
              </div>
            </>
          ) : (
            /* Success State */
            <div className="text-center space-y-6">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-10 h-10 text-success" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  API Generated Successfully!
                </h3>
                <p className="text-muted-foreground">
                  We hope this solution adds value to your project. We're always here to support your success.
                </p>
              </div>
              <div className="flex gap-3">
              {/*  <Button
                  onClick={handleDownload}
                  className="flex-1 bg-primary hover:bg-primary-dark text-primary-foreground"
                >
                  Download Project
                </Button>*/}
                <Button variant="outline" onClick={handleReset}className="flex-1 bg-primary hover:bg-primary-dark text-primary-foreground">
                  Upload New File
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
